import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import MorningTide from './MorningTide';
import './Page.css';
import './DeStress.css';

const POP_COLORS = ['#ff8ba7', '#ffc56e', '#f7ef88', '#79d8b2', '#72bde7', '#ad91e8'];

const playPop = (pitch = 240) => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(pitch, context.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(pitch * 0.55, context.currentTime + 0.09);
  gain.gain.setValueAtTime(0.12, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.12);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.12);
  oscillator.addEventListener('ended', () => context.close());
};

const playTone = (pitch = 420, duration = .45) => {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(pitch, context.currentTime);
  gain.gain.setValueAtTime(.055, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(.001, context.currentTime + duration);
  oscillator.connect(gain); gain.connect(context.destination);
  oscillator.start(); oscillator.stop(context.currentTime + duration);
  oscillator.addEventListener('ended', () => context.close());
};

const PopIt = () => {
  const [popped, setPopped] = useState(() => new Set());
  const dragging = useRef(false);
  const bubbles = useMemo(() => Array.from({ length: 36 }, (_, index) => index), []);

  const toggle = (index, popOnly = false) => {
    setPopped(previous => {
      const next = new Set(previous);
      if (next.has(index) && !popOnly) next.delete(index);
      else next.add(index);
      return next;
    });
    playPop(210 + (index % 6) * 25);
    navigator.vibrate?.(8);
  };

  return (
    <section className="game-panel" aria-labelledby="pop-title">
      <div className="game-panel-heading">
        <div>
          <span className="game-kicker">tap away the noise</span>
          <h2 id="pop-title">Pocket Pop-It</h2>
        </div>
        <button className="soft-button" onClick={() => setPopped(new Set())}>Reset</button>
      </div>
      <div
        className={`pop-board ${popped.size === 36 ? 'is-complete' : ''}`}
        aria-label="Interactive pop-it board"
        onPointerUp={() => { dragging.current = false; }}
        onPointerLeave={() => { dragging.current = false; }}
      >
        {bubbles.map(index => (
          <button
            key={index}
            className={`pop-bubble ${popped.has(index) ? 'is-popped' : ''}`}
            style={{ '--bubble-color': POP_COLORS[Math.floor(index / 6)] }}
            onPointerDown={(event) => { event.preventDefault(); dragging.current = true; toggle(index); }}
            onPointerEnter={() => { if (dragging.current && !popped.has(index)) toggle(index, true); }}
            onClick={(event) => { if (event.detail === 0) toggle(index); }}
            aria-label={`Bubble ${index + 1}${popped.has(index) ? ', popped' : ''}`}
            aria-pressed={popped.has(index)}
          ><span className="pop-bubble-shine" /><span className="pop-bubble-ring" /></button>
        ))}
      </div>
      <div className="pop-progress"><span style={{ width: `${(popped.size / 36) * 100}%` }} /></div>
      <p className="game-note">{popped.size === 36 ? 'Perfect sweep. Tap reset and go again ✨' : `${popped.size} of 36 · click or drag across them`}</p>
    </section>
  );
};

const EndlessDrive = () => {
  const canvasHost = useRef(null);
  const input = useRef({ left: false, right: false, boost: false, brake: false });
  const [speed, setSpeed] = useState(72);
  const [distance, setDistance] = useState(0);
  const [offRoad, setOffRoad] = useState(false);
  const [crashes, setCrashes] = useState(0);
  const [impact, setImpact] = useState(false);

  useEffect(() => {
    const host = canvasHost.current;
    if (!host) return undefined;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x7edcff);
    scene.fog = new THREE.FogExp2(0xa9e5e9, .0095);
    const camera = new THREE.PerspectiveCamera(58, host.clientWidth / host.clientHeight, 0.1, 260);
    camera.position.set(0, 4.8, 10.5);
    camera.lookAt(0, 1.1, -18);
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.7));
    renderer.setSize(host.clientWidth, host.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.32;
    host.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xcaf7ff, 0x45622c, 2.5));
    const sunLight = new THREE.DirectionalLight(0xfff0c2, 4.2);
    sunLight.position.set(-12, 24, 8);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(1024, 1024);
    scene.add(sunLight);
    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(210, 32, 18),
      new THREE.ShaderMaterial({
        side: THREE.BackSide,
        uniforms: { topColor: { value: new THREE.Color(0x36bff5) }, horizonColor: { value: new THREE.Color(0xffd7a0) }, bottomColor: { value: new THREE.Color(0x8bd6cc) } },
        vertexShader: 'varying vec3 vWorldPosition; void main(){ vec4 worldPosition=modelMatrix*vec4(position,1.0); vWorldPosition=worldPosition.xyz; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }',
        fragmentShader: 'uniform vec3 topColor; uniform vec3 horizonColor; uniform vec3 bottomColor; varying vec3 vWorldPosition; void main(){ float h=normalize(vWorldPosition).y; vec3 low=mix(bottomColor,horizonColor,smoothstep(-0.15,0.12,h)); vec3 color=mix(low,topColor,smoothstep(0.02,0.72,h)); gl_FragColor=vec4(color,1.0); }',
      })
    );
    scene.add(sky);
    const sun = new THREE.Mesh(new THREE.SphereGeometry(5, 40, 40), new THREE.MeshBasicMaterial({ color: 0xffefaa, fog: false }));
    sun.position.set(-28, 23, -105);
    scene.add(sun);
    const sunHalo = new THREE.PointLight(0xffc36e, 18, 95, 1.5);
    sunHalo.position.copy(sun.position);
    scene.add(sunHalo);

    const mountainMaterial = new THREE.MeshStandardMaterial({ color: 0x596c87, roughness: 1, flatShading: true });
    const mountains = [];
    for (let i = 0; i < 18; i += 1) {
      const mountain = new THREE.Mesh(new THREE.ConeGeometry(10 + (i % 5) * 3.8, 18 + (i % 4) * 7, 5), mountainMaterial);
      const side = i % 2 ? 1 : -1;
      mountain.position.set(side * (22 + (i % 6) * 10), 6, -45 - Math.floor(i / 2) * 13);
      mountain.userData.baseX = mountain.position.x;
      mountain.userData.baseZ = mountain.position.z;
      mountain.userData.parallax = .12 + (i % 4) * .035;
      mountain.rotation.y = i * .61;
      scene.add(mountain);
      mountains.push(mountain);
    }

    const roadMaterial = new THREE.MeshStandardMaterial({ color: 0x303b4a, roughness: .88 });
    const grassMaterial = new THREE.MeshStandardMaterial({ color: 0x63a85a, roughness: 1 });
    const roadSegments = [];
    const segmentLength = 6;
    const segmentVisualLength = 9.4;
    for (let i = 0; i < 36; i += 1) {
      const group = new THREE.Group();
      const road = new THREE.Mesh(new THREE.BoxGeometry(13, .18, segmentVisualLength), roadMaterial);
      road.receiveShadow = true;
      group.add(road);
      [-9.5, 9.5].forEach(x => {
        const grass = new THREE.Mesh(new THREE.BoxGeometry(6, .12, segmentVisualLength), grassMaterial);
        grass.position.set(x, -.04, 0);
        grass.receiveShadow = true;
        group.add(grass);
      });
      [-3.25, 3.25].forEach(x => {
        const dash = new THREE.Mesh(new THREE.BoxGeometry(.16, .04, 2.15), new THREE.MeshBasicMaterial({ color: 0xfff3c4 }));
        dash.position.set(x, .13, 0);
        group.add(dash);
      });
      [-6.25, 6.25].forEach(x => {
        const edge = new THREE.Mesh(new THREE.BoxGeometry(.22, .05, segmentVisualLength), new THREE.MeshBasicMaterial({ color: 0xffffff }));
        edge.position.set(x, .14, 0);
        group.add(edge);
      });
      group.position.z = -i * segmentLength;
      scene.add(group);
      roadSegments.push(group);
    }

    const treeTrunkMaterial = new THREE.MeshStandardMaterial({ color: 0x6b442d });
    const treeLeafMaterials = [0x1f7a52, 0x2c8a5b, 0x3d9257].map(color => new THREE.MeshStandardMaterial({ color, roughness: .9, flatShading: true }));
    const roadside = [];
    for (let i = 0; i < 34; i += 1) {
      const tree = new THREE.Group();
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(.18, .25, 1.8, 7), treeTrunkMaterial);
      trunk.position.y = .9;
      const leaves = new THREE.Mesh(new THREE.ConeGeometry(1.25 + (i % 3) * .18, 3.1, 9), treeLeafMaterials[i % treeLeafMaterials.length]);
      const crown = new THREE.Mesh(new THREE.ConeGeometry(.92 + (i % 2) * .15, 2.5, 9), treeLeafMaterials[(i + 1) % treeLeafMaterials.length]);
      leaves.position.y = 2.75; crown.position.y = 4.15;
      leaves.castShadow = true; crown.castShadow = true;
      tree.add(trunk, leaves, crown);
      tree.userData.roadsideOffset = (i % 2 ? 1 : -1) * (9 + (i % 4) * 2.2);
      tree.position.set(tree.userData.roadsideOffset, 0, -8 - i * 6.4);
      tree.scale.setScalar(.8 + (i % 4) * .1);
      scene.add(tree);
      roadside.push(tree);
    }

    const car = new THREE.Group();
    const red = new THREE.MeshPhysicalMaterial({ color: 0xff3e4f, metalness: .62, roughness: .2, clearcoat: 1, clearcoatRoughness: .12 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x17202d, roughness: .4 });
    const glass = new THREE.MeshPhysicalMaterial({ color: 0x75dfff, metalness: .1, roughness: .06, transmission: .18, transparent: true, opacity: .88 });
    const bodyShape = new THREE.Shape();
    bodyShape.moveTo(-1.25,-.25); bodyShape.quadraticCurveTo(-1.35,.15,-1.05,.45); bodyShape.lineTo(1.05,.45); bodyShape.quadraticCurveTo(1.35,.15,1.25,-.25); bodyShape.closePath();
    const bodyGeometry = new THREE.ExtrudeGeometry(bodyShape, { depth: 3.7, bevelEnabled: true, bevelSegments: 4, steps: 1, bevelSize: .12, bevelThickness: .12 });
    bodyGeometry.translate(0,0,-1.85);
    const body = new THREE.Mesh(bodyGeometry, red);
    body.position.y = .72; body.castShadow = true;
    const hood = new THREE.Mesh(new THREE.BoxGeometry(2.15, .28, 1.4, 3, 1, 3), red);
    hood.position.set(0, 1.17, -1.05); hood.castShadow = true;
    const cabinShape = new THREE.Shape();
    cabinShape.moveTo(-.9,0); cabinShape.lineTo(-.62,.65); cabinShape.quadraticCurveTo(0,.9,.62,.65); cabinShape.lineTo(.9,0); cabinShape.closePath();
    const cabinGeometry = new THREE.ExtrudeGeometry(cabinShape, { depth: 1.55, bevelEnabled: true, bevelSegments: 3, bevelSize: .08, bevelThickness: .08 });
    cabinGeometry.translate(0,0,-.75);
    const cabin = new THREE.Mesh(cabinGeometry, glass);
    cabin.position.set(0, 1.12, .25);
    const spoiler = new THREE.Mesh(new THREE.BoxGeometry(2.3,.12,.34), dark);
    spoiler.position.set(0,1.4,1.75);
    const spoilerStem = new THREE.Mesh(new THREE.BoxGeometry(1.65,.28,.1),dark);
    spoilerStem.position.set(0,1.22,1.68);
    car.add(body, hood, cabin, spoiler, spoilerStem);
    const tailMaterial = new THREE.MeshBasicMaterial({ color:0xff1f2d });
    [-.78,.78].forEach(x => { const tail = new THREE.Mesh(new THREE.BoxGeometry(.42,.16,.06),tailMaterial); tail.position.set(x,.92,1.93); car.add(tail); });
    [[-1.2,.45,-1.25],[1.2,.45,-1.25],[-1.2,.45,1.3],[1.2,.45,1.3]].forEach(([x,y,z]) => {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(.42,.42,.32,18), dark);
      wheel.rotation.z = Math.PI / 2; wheel.position.set(x,y,z); car.add(wheel);
    });
    car.position.set(0, .12, 1.8);
    scene.add(car);

    const obstacles = [];
    const coneOrange = new THREE.MeshStandardMaterial({ color: 0xff6a2a, roughness: .55 });
    const coneWhite = new THREE.MeshStandardMaterial({ color: 0xfff8e8, roughness: .65 });
    const barrierRed = new THREE.MeshStandardMaterial({ color: 0xe83d4f, roughness: .5 });
    for (let i = 0; i < 7; i += 1) {
      const obstacle = new THREE.Group();
      if (i % 4 === 0) {
        const bar = new THREE.Mesh(new THREE.BoxGeometry(2.3, .75, .45), barrierRed);
        bar.position.y = .52; bar.castShadow = true;
        const stripe = new THREE.Mesh(new THREE.BoxGeometry(1.2, .78, .47), coneWhite);
        stripe.position.y = .52; stripe.rotation.z = -.28;
        obstacle.add(bar, stripe);
      } else {
        const cone = new THREE.Mesh(new THREE.ConeGeometry(.42, 1.25, 14), coneOrange);
        cone.position.y = .63; cone.castShadow = true;
        const band = new THREE.Mesh(new THREE.CylinderGeometry(.31, .36, .2, 14), coneWhite);
        band.position.y = .49;
        const base = new THREE.Mesh(new THREE.BoxGeometry(1,.12,.85), dark);
        base.position.y = .06;
        obstacle.add(cone, band, base);
      }
      obstacle.userData.lane = [-3.3, 0, 3.3][i % 3];
      obstacle.userData.hit = false;
      obstacle.position.set(obstacle.userData.lane, 0, -58 - i * 44);
      scene.add(obstacle); obstacles.push(obstacle);
    }

    const streakGeometry = new THREE.BufferGeometry();
    const streakPositions = [];
    for (let i = 0; i < 80; i += 1) {
      const x = (Math.random() - .5) * 34;
      const y = .3 + Math.random() * 7;
      const z = -Math.random() * 90;
      streakPositions.push(x, y, z, x, y, z + 1.8 + Math.random() * 3.2);
    }
    streakGeometry.setAttribute('position', new THREE.Float32BufferAttribute(streakPositions, 3));
    const speedStreaks = new THREE.LineSegments(streakGeometry, new THREE.LineBasicMaterial({ color: 0xeaffff, transparent: true, opacity: 0 }));
    scene.add(speedStreaks);

    const keyMap = event => {
      const key = event.key.toLowerCase();
      return { left: key === 'arrowleft' || key === 'a', right: key === 'arrowright' || key === 'd', boost: key === 'arrowup' || key === 'w', brake: key === 'arrowdown' || key === 's' };
    };
    const keyDown = event => {
      const mapped = keyMap(event);
      Object.entries(mapped).forEach(([name, match]) => { if (match) input.current[name] = true; });
      if (Object.values(mapped).some(Boolean)) event.preventDefault();
    };
    const keyUp = event => { Object.entries(keyMap(event)).forEach(([name, match]) => { if (match) input.current[name] = false; }); };
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    const resize = () => { camera.aspect = host.clientWidth / host.clientHeight; camera.updateProjectionMatrix(); renderer.setSize(host.clientWidth, host.clientHeight); };
    const observer = new ResizeObserver(resize); observer.observe(host);
    const clock = new THREE.Clock();
    let currentSpeed = 24;
    let steering = 0;
    let lateralVelocity = 0;
    let traveled = 0;
    let hudElapsed = 0;
    let impactCooldown = 0;
    let frame;
    const animate = () => {
      const dt = Math.min(clock.getDelta(), .033);
      const roadCurve = value => Math.sin(value * .026) * 9.5 + Math.sin(value * .011 + 1.7) * 5.5 + Math.sin(value * .052) * 1.8;
      const roadHeight = value => Math.sin(value * .011 + .6) * 2.8 + Math.sin(value * .0042) * 1.9;
      const curveOrigin = roadCurve(traveled);
      const heightOrigin = roadHeight(traveled);
      const turnSlope = (roadCurve(traveled + 16) - curveOrigin) / 16;
      const hillSlope = (roadHeight(traveled + 14) - heightOrigin) / 14;
      const currentlyOffRoad = Math.abs(car.position.x) > 5.15;
      const targetSpeed = impactCooldown > 0 ? 8 : currentlyOffRoad ? 10 : input.current.boost ? 52 : input.current.brake ? 7 : 24;
      currentSpeed = THREE.MathUtils.damp(currentSpeed, targetSpeed, input.current.boost ? 2.8 : 3.8, dt);
      const steerTarget = (input.current.right ? 1 : 0) - (input.current.left ? 1 : 0);
      steering = THREE.MathUtils.damp(steering, steerTarget, 10, dt);
      const speedRatio = THREE.MathUtils.clamp(currentSpeed / 52, 0, 1);
      const steeringPower = THREE.MathUtils.lerp(48, 34, speedRatio);
      const centrifugalForce = turnSlope * currentSpeed * THREE.MathUtils.lerp(.52, .78, speedRatio);
      lateralVelocity += (steering * steeringPower - centrifugalForce) * dt;
      const tireGrip = currentlyOffRoad ? 1.25 : THREE.MathUtils.lerp(3.0, 2.0, speedRatio);
      lateralVelocity *= Math.exp(-tireGrip * dt);
      car.position.x = THREE.MathUtils.clamp(car.position.x + lateralVelocity * dt, -7.2, 7.2);
      car.rotation.z = THREE.MathUtils.damp(car.rotation.z, -lateralVelocity * .026 - steering * .045, 7, dt);
      car.rotation.y = THREE.MathUtils.damp(car.rotation.y, -turnSlope * .22 - steering * .045, 4.5, dt);
      car.rotation.x = THREE.MathUtils.damp(car.rotation.x, -hillSlope * .7, 4.2, dt);
      const rumble = currentlyOffRoad ? Math.sin(traveled * 2.8) * .055 : 0;
      const suspensionBob = Math.sin(traveled * .48) * .018 * speedRatio;
      car.position.y = THREE.MathUtils.damp(car.position.y, (currentlyOffRoad ? .23 : .12) + Math.abs(rumble) + suspensionBob, 12, dt);
      camera.position.x = THREE.MathUtils.damp(camera.position.x, car.position.x * .28 + rumble, 2.8, dt);
      camera.rotation.z = THREE.MathUtils.damp(camera.rotation.z, -steering * .014 + turnSlope * .025, 3, dt);
      camera.rotation.y = THREE.MathUtils.damp(camera.rotation.y, -turnSlope * .18, 2.6, dt);
      camera.rotation.x = THREE.MathUtils.damp(camera.rotation.x, -.13 - hillSlope * .32, 2.2, dt);
      roadSegments.forEach(segment => {
        segment.position.z += currentSpeed * dt;
        if (segment.position.z > 12) segment.position.z -= roadSegments.length * segmentLength;
        const ahead = -segment.position.z;
        const here = roadCurve(traveled + ahead) - curveOrigin;
        const next = roadCurve(traveled + ahead + segmentLength) - curveOrigin;
        const hereHeight = roadHeight(traveled + ahead) - heightOrigin;
        const nextHeight = roadHeight(traveled + ahead + segmentLength) - heightOrigin;
        segment.position.x = here;
        segment.position.y = hereHeight;
        segment.rotation.y = Math.atan2(next - here, segmentLength);
        segment.rotation.x = Math.atan2(nextHeight - hereHeight, segmentLength);
        const afterNext = roadCurve(traveled + ahead + segmentLength * 2) - curveOrigin;
        const bendDelta = (afterNext - next) - (next - here);
        segment.rotation.z = THREE.MathUtils.clamp(-bendDelta * .035, -.055, .055);
      });
      roadside.forEach(tree => {
        tree.position.z += currentSpeed * dt;
        if (tree.position.z > 14) tree.position.z -= 218;
        tree.position.x = tree.userData.roadsideOffset + roadCurve(traveled - tree.position.z) - curveOrigin;
        tree.position.y = roadHeight(traveled - tree.position.z) - heightOrigin;
      });
      obstacles.forEach(obstacle => {
        obstacle.position.z += currentSpeed * dt;
        if (obstacle.position.z > 14) {
          obstacle.position.z -= 308;
          obstacle.userData.lane = [-3.3, 0, 3.3][Math.floor(Math.random() * 3)];
          obstacle.userData.hit = false;
          obstacle.rotation.set(0, 0, 0);
        }
        obstacle.position.x = obstacle.userData.lane + roadCurve(traveled - obstacle.position.z) - curveOrigin;
        obstacle.position.y = roadHeight(traveled - obstacle.position.z) - heightOrigin + .1;
        if (!obstacle.userData.hit && obstacle.position.z > -.7 && obstacle.position.z < 3.3 && Math.abs(car.position.x - obstacle.userData.lane) < 1.25) {
          obstacle.userData.hit = true;
          obstacle.rotation.x = 1.25;
          obstacle.rotation.z = (car.position.x > obstacle.userData.lane ? -1 : 1) * .8;
          lateralVelocity += (car.position.x > obstacle.userData.lane ? 1 : -1) * 5;
          currentSpeed *= .32;
          impactCooldown = .65;
          setCrashes(value => value + 1);
          setImpact(true);
          window.setTimeout(() => setImpact(false), 320);
          navigator.vibrate?.([45, 25, 65]);
        }
      });
      impactCooldown = Math.max(0, impactCooldown - dt);
      mountains.forEach(mountain => {
        mountain.position.z += currentSpeed * dt * mountain.userData.parallax;
        if (mountain.position.z > -18) mountain.position.z -= 125;
        const mountainCurve = roadCurve(traveled - mountain.position.z) - curveOrigin;
        mountain.position.x = mountain.userData.baseX + mountainCurve * .35 - car.position.x * mountain.userData.parallax;
      });
      const speedMix = THREE.MathUtils.clamp((currentSpeed - 7) / 45, 0, 1);
      camera.fov = THREE.MathUtils.damp(camera.fov, 54 + speedMix * 18, 3.5, dt);
      camera.position.y = THREE.MathUtils.damp(camera.position.y, 5 - speedMix * .65, 3, dt);
      camera.updateProjectionMatrix();
      speedStreaks.material.opacity = THREE.MathUtils.smoothstep(currentSpeed, 30, 52) * .42;
      speedStreaks.position.z += currentSpeed * dt * 1.7;
      if (speedStreaks.position.z > 28) speedStreaks.position.z = -35;
      traveled += currentSpeed * dt;
      hudElapsed += dt;
      if (hudElapsed > .12) { setSpeed(Math.round(currentSpeed * 3.1)); setDistance(traveled / 1600); setOffRoad(currentlyOffRoad); hudElapsed = 0; }
      renderer.render(scene, camera);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
      scene.traverse(object => { object.geometry?.dispose(); if (Array.isArray(object.material)) object.material.forEach(item => item.dispose()); else object.material?.dispose(); });
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  const hold = (name, value) => { input.current[name] = value; };

  return (
    <section className={`drive-panel drive-panel-3d ${impact ? 'has-impact' : ''}`} aria-labelledby="drive-title">
      <div className="drive-hud">
        <div><span className="game-kicker">endless coast</span><h2 id="drive-title">Golden Hour Drive</h2></div>
        <div className="drive-stats"><span><strong>{Math.round(speed)}</strong> mph</span><span><strong>{distance.toFixed(1)}</strong> mi</span><span className="impact-stat"><strong>{crashes}</strong> hits</span></div>
      </div>
      <div className="drive-world drive-world-3d" ref={canvasHost}>
        <div className={`offroad-warning ${offRoad ? 'show' : ''}`}>OFF ROAD · STEER BACK</div>
        <div className="drive-help">A D / ← → steer&nbsp;&nbsp; • &nbsp;&nbsp;W S / ↑ ↓ speed</div>
        <div className="drive-touch-controls">
          <div>
            <button onPointerDown={() => hold('left', true)} onPointerUp={() => hold('left', false)} onPointerCancel={() => hold('left', false)} aria-label="Steer left">←</button>
            <button onPointerDown={() => hold('right', true)} onPointerUp={() => hold('right', false)} onPointerCancel={() => hold('right', false)} aria-label="Steer right">→</button>
          </div>
          <div>
            <button onPointerDown={() => hold('brake', true)} onPointerUp={() => hold('brake', false)} onPointerCancel={() => hold('brake', false)} aria-label="Brake">↓</button>
            <button className="gas-button" onPointerDown={() => hold('boost', true)} onPointerUp={() => hold('boost', false)} onPointerCancel={() => hold('boost', false)} aria-label="Accelerate">↑</button>
          </div>
        </div>
      </div>
    </section>
  );
};

const BREATH_PHASES = [
  { label: 'Breathe in', duration: 4 },
  { label: 'Hold', duration: 4 },
  { label: 'Breathe out', duration: 6 },
  { label: 'Rest', duration: 2 },
];

const BreathingOrb = () => {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(0);
  const [seconds, setSeconds] = useState(BREATH_PHASES[0].duration);
  const [cycles, setCycles] = useState(0);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      setSeconds(value => {
        if (value > 1) return value - 1;
        setPhase(current => {
          const next = (current + 1) % BREATH_PHASES.length;
          if (next === 0) setCycles(value => value + 1);
          playTone([440, 520, 330, 390][next], .55);
          window.setTimeout(() => setSeconds(BREATH_PHASES[next].duration), 0);
          return next;
        });
        return 0;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  const reset = () => {
    setRunning(false);
    setPhase(0);
    setSeconds(BREATH_PHASES[0].duration);
    setCycles(0);
  };

  return (
    <section className="game-panel breath-panel" aria-labelledby="breath-title">
      <div className="game-panel-heading">
        <div>
          <span className="game-kicker">one minute reset</span>
          <h2 id="breath-title">Breathing Orb</h2>
        </div>
        <button className="soft-button" onClick={reset}>Reset</button>
      </div>
      <div className={`breath-space phase-${phase} ${running ? 'is-running' : ''}`}>
        <div className="breath-aurora breath-aurora-one" /><div className="breath-aurora breath-aurora-two" />
        <div className="breath-stars" aria-hidden="true">{Array.from({ length: 18 }, (_, index) => <i key={index} style={{ '--i': index, '--x': `${8 + index * 5.1}%`, '--y': `${12 + ((index * 37) % 73)}%` }} />)}</div>
        <div className="breath-ripple breath-ripple-one" />
        <div className="breath-ripple breath-ripple-two" />
        <div className="breath-orbit breath-orbit-one"><i /></div>
        <div className="breath-orbit breath-orbit-two"><i /></div>
        <div className="breath-orbit breath-orbit-three"><i /></div>
        <div className="breath-progress-ring" style={{ '--breath-progress': `${running ? ((BREATH_PHASES[phase].duration - seconds) / BREATH_PHASES[phase].duration) * 360 : 0}deg` }} />
        <button className="breath-orb" onClick={() => setRunning(value => !value)} aria-label={running ? 'Pause breathing exercise' : 'Start breathing exercise'}>
          <i className="breath-glass" />
          <i className="breath-core" />
          <strong>{running ? BREATH_PHASES[phase].label : 'Begin'}</strong>
          <span>{running ? seconds : 'tap'}</span>
        </button>
      </div>
      <div className="breath-status"><span>{cycles} {cycles === 1 ? 'cycle' : 'cycles'}</span><span>{running ? `${BREATH_PHASES[phase].label} · ${seconds}s` : 'Ready when you are'}</span></div>
      <p className="game-note">{running ? 'Follow the orb. Let your shoulders drop.' : 'Tap the orb when you are ready.'}</p>
    </section>
  );
};

const DeStress = () => {
  const [activeGame, setActiveGame] = useState('pop');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const arcadeRef = useRef(null);
  const stageRef = useRef(null);
  useEffect(() => {
    const update = () => setIsFullscreen(document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', update);
    return () => document.removeEventListener('fullscreenchange', update);
  }, []);

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) await stageRef.current?.requestFullscreen();
    else await document.exitFullscreen();
  };
  const games = [
    { id: 'pop', icon: '●', title: 'Pop It', description: 'Tactile, colorful, endlessly resettable.' },
    { id: 'drive', icon: '⌁', title: 'Endless Drive', description: 'Cruise a glowing coast with no finish line.' },
    { id: 'breathe', icon: '◌', title: 'Breathing Orb', description: 'A calm guided breathing rhythm.' },
  ];

  return (
    <div
      className={`page destress-page mode-${activeGame}`}
      ref={arcadeRef}
      onPointerMove={(event) => {
        const bounds = arcadeRef.current?.getBoundingClientRect();
        if (!bounds) return;
        arcadeRef.current.style.setProperty('--mouse-x', `${event.clientX - bounds.left}px`);
        arcadeRef.current.style.setProperty('--mouse-y', `${event.clientY - bounds.top}px`);
      }}
    >
      <MorningTide />
      <div className="arcade-ambience" aria-hidden="true">
        <span className="ambient-orb ambient-orb-one" />
        <span className="ambient-orb ambient-orb-two" />
        <span className="ambient-grid" />
        <span className="ambient-noise" />
      </div>
      <Navigation />
      <main className="page-content destress-content">
        <Link to="/chat" className="echo-float-cta" aria-label="Talk to Echo"><span className="echo-float-face">◉</span><span><strong>Need to talk?</strong><small>Go to Echo →</small></span></Link>
        <span className="destress-eyebrow"><i /> enter the reset room <i /></span>
        <h1 className="page-title destress-title">De-Stress <em>Arcade</em></h1>
        <p className="destress-subtitle">No scores to chase. No streaks to protect. Just pick something that feels good.</p>

        <div className="game-picker" role="tablist" aria-label="Choose a de-stress activity">
          {games.map(game => (
            <button
              key={game.id}
              role="tab"
              aria-selected={activeGame === game.id}
              className={`game-choice ${activeGame === game.id ? 'active' : ''}`}
              onClick={() => setActiveGame(game.id)}
            >
              <span className="game-choice-icon">{game.icon}</span>
              <span><strong>{game.title}</strong><small>{game.description}</small></span>
            </button>
          ))}
        </div>

        <div className="game-stage" role="tabpanel" ref={stageRef}>
          <button className="fullscreen-toggle" onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
            <span>{isFullscreen ? '↙' : '↗'}</span>{isFullscreen ? 'Exit' : 'Fullscreen'}
          </button>
          {activeGame === 'pop' && <PopIt />}
          {activeGame === 'drive' && <EndlessDrive />}
          {activeGame === 'breathe' && <BreathingOrb />}
        </div>

        <p className="destress-reminder">These activities are for a quick reset—not a replacement for professional support.</p>
      </main>
    </div>
  );
};

export default DeStress;
